import type { ChangeEvent, MouseEvent } from 'react'
import { rankGlobalFuzzy } from '../fns/sortingFns'
import type { DataTableInstance, DataTableRow, DataTableRowData } from '../types'
import { parseFromValuesOrFunc } from './utils'

export const getCanRankRows = <TData extends DataTableRowData>(table: DataTableInstance<TData>) => {
  const {
    getState,
    options: {
      enableGlobalFilterRankedResults,
      manualExpanding,
      manualFiltering,
      manualGrouping,
      manualSorting,
    },
  } = table
  const { expanded, globalFilterFn } = getState()

  return (
    !manualExpanding &&
    !manualFiltering &&
    !manualGrouping &&
    !manualSorting &&
    enableGlobalFilterRankedResults &&
    globalFilterFn === 'fuzzy' &&
    expanded !== true &&
    !Object.values(expanded).some(Boolean)
  )
}

export const getIsRankingRows = <TData extends DataTableRowData>(
  table: DataTableInstance<TData>,
) => {
  const { globalFilter, sorting } = table.getState()

  return getCanRankRows(table) && globalFilter && !Object.values(sorting).some(Boolean)
}

export const getTableRows = <TData extends DataTableRowData>(
  table: DataTableInstance<TData>,
  all?: boolean,
): DataTableRow<TData>[] => {
  const {
    getCenterRows,
    getPrePaginationRowModel,
    getRowModel,
    getState,
    getTopRows,
    options: {
      enablePagination,
      enableRowPinning,
      manualPagination,
      positionCreatingRow,
      rowPinningDisplayMode,
    },
  } = table
  const { creatingRow, pagination } = getState()

  const isRankingRows = getIsRankingRows(table)

  let rows: DataTableRow<TData>[] = []
  if (!isRankingRows) {
    if (!enableRowPinning || rowPinningDisplayMode?.includes('sticky')) {
      if (all) {
        rows = getPrePaginationRowModel().rows
      } else {
        rows = getRowModel().rows
      }
    } else {
      rows = getCenterRows()
    }
  } else {
    // fuzzy ranking adjustments
    rows = getPrePaginationRowModel().rows.sort((a, b) => rankGlobalFuzzy(a, b))
    if (enablePagination && !manualPagination && !all) {
      const start = pagination.pageIndex * pagination.pageSize
      rows = rows.slice(start, start + pagination.pageSize)
    }
    if (enableRowPinning && !rowPinningDisplayMode?.includes('sticky')) {
      // "re-center-ize" the rows (no top or bottom pinned rows unless sticky)
      rows = rows.filter((row) => !row.getIsPinned())
    }
  }
  // row pinning adjustments
  if (enableRowPinning && rowPinningDisplayMode?.includes('sticky')) {
    const centerPinnedRowIds = rows.filter((row) => row.getIsPinned()).map((r) => r.id)

    rows = [...getTopRows().filter((row) => !centerPinnedRowIds.includes(row.id)), ...rows]
  }
  // blank inserted creating row adjustments
  if (positionCreatingRow !== undefined && creatingRow) {
    let creatingRowIndex: number

    if (!Number.isNaN(+positionCreatingRow)) {
      // If `positionCreatingRow` is a number, use it as an index
      creatingRowIndex = +positionCreatingRow
    } else if (positionCreatingRow === 'top') {
      // If `positionCreatingRow` is 'top', set the index to 0
      creatingRowIndex = 0
    } else {
      // Otherwise, set the index to the length of rows (i.e., add to the end)
      creatingRowIndex = rows.length
    }

    // Insert the creatingRow at the computed index
    rows = [...rows.slice(0, creatingRowIndex), creatingRow, ...rows.slice(creatingRowIndex)]
  }

  return rows
}

export const getIsRowSelected = <TData extends DataTableRowData>({
  row,
  table,
}: {
  row: DataTableRow<TData>
  table: DataTableInstance<TData>
}) => {
  const {
    options: { enableRowSelection },
  } = table

  return (
    row.getIsSelected() ||
    (parseFromValuesOrFunc(enableRowSelection, row) &&
      row.getCanSelectSubRows() &&
      row.getIsAllSubRowsSelected())
  )
}

export const getTableRowselectionHandler =
  <TData extends DataTableRowData>({
    renderedRowIndex = 0,
    row,
    table,
  }: {
    renderedRowIndex?: number
    row: DataTableRow<TData>
    table: DataTableInstance<TData>
  }) =>
  (event: ChangeEvent<HTMLInputElement> | MouseEvent<HTMLTableRowElement>, value?: boolean) => {
    const {
      getState,
      options: {
        enableBatchRowSelection,
        enableMultiRowSelection,
        enableRowPinning,
        manualPagination,
        rowPinningDisplayMode,
      },
      refs: { lastSelectedRowId },
    } = table
    const {
      pagination: { pageIndex, pageSize },
    } = getState()

    const paginationOffset = manualPagination ? 0 : pageSize * pageIndex

    const wasCurrentRowChecked = getIsRowSelected({ row, table })

    // toggle selection of this row
    row.toggleSelected(value ?? !wasCurrentRowChecked)

    const changedRowIds = new Set<string>([row.id])

    // if shift key is pressed, select all rows between last selected and this one
    if (
      enableBatchRowSelection &&
      enableMultiRowSelection &&
      // biome-ignore lint/suspicious/noExplicitAny: TODO - fix type definition
      (event as any).nativeEvent.shiftKey &&
      lastSelectedRowId.current !== null
    ) {
      const rows = getTableRows(table, true)

      const lastIndex = rows.findIndex((r) => r.id === lastSelectedRowId.current)

      if (lastIndex !== -1) {
        const isLastIndexChecked = getIsRowSelected({
          row: rows?.[lastIndex],
          table,
        })

        const currentIndex = renderedRowIndex + paginationOffset
        const [start, end] =
          lastIndex < currentIndex ? [lastIndex, currentIndex] : [currentIndex, lastIndex]

        // toggle selection of all rows between last selected and this one
        // but only if the last selected row is not the same as the current one
        if (wasCurrentRowChecked !== isLastIndexChecked) {
          for (let i = start; i <= end; i++) {
            rows[i].toggleSelected(!wasCurrentRowChecked)
            changedRowIds.add(rows[i].id)
          }
        }
      }
    }

    // record the last selected row id
    lastSelectedRowId.current = row.id

    // if all sub rows were selected, unselect them
    if (row.getCanSelectSubRows() && row.getIsAllSubRowsSelected()) {
      row.subRows?.forEach((r) => r.toggleSelected(false))
    }

    if (enableRowPinning && rowPinningDisplayMode?.includes('select')) {
      changedRowIds.forEach((rowId) => {
        const rowToTogglePin = table.getRow(rowId)
        let pinPosition: 'top' | 'bottom' | false = false

        if (!wasCurrentRowChecked) {
          pinPosition = rowPinningDisplayMode?.includes('bottom') ? 'bottom' : 'top'
        }

        rowToTogglePin.pin(pinPosition)
      })
    }
  }

export const getTableSelectAllHandler =
  <TData extends DataTableRowData>({ table }: { table: DataTableInstance<TData> }) =>
  (
    event: ChangeEvent<HTMLInputElement> | MouseEvent<HTMLButtonElement>,
    value?: boolean,
    forceAll?: boolean,
  ) => {
    const {
      options: { enableRowPinning, rowPinningDisplayMode, selectAllMode },
      refs: { lastSelectedRowId },
    } = table

    if (selectAllMode === 'all' || forceAll) {
      // @ts-expect-error this handles the checkbox
      table.toggleAllRowsSelected(value ?? (event as unknown).target.checked)
    } else {
      // @ts-expect-error this handles the checkbox
      table.toggleAllPageRowsSelected(value ?? (event as unknown).target.checked)
    }
    if (enableRowPinning && rowPinningDisplayMode?.includes('select')) {
      table.setRowPinning({ bottom: [], top: [] })
    }
    lastSelectedRowId.current = null
  }
